/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { accessAPI, formatFecha, Sidebar, TablaFiltrable } from "../../../shared";
import { Formulario } from "../../../shared";
import { Boton } from "../../../shared";
import { Titulo } from "../../../shared";
import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { confirmDialog } from "primereact/confirmdialog";

export default function FormularioEnsayo({
  pasoId,
  normaId,
  reportesEnActividad,
  modoLectura,
}) {
  const [loaderAgregarEnsayo, setLoaderAgregarEnsayo] = useState(false);

  const [reportes, setReportes] = useState(reportesEnActividad);

  const [sideBar, setSideBar] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [reporteSeleccionado, setReporteSeleccionado] = useState(null);

  const toast = useRef(null);
  const menuRef = useRef(null);
  const [reporteMenu, setReporteMenu] = useState(null);

  // Configuración del formulario para agregar/editar ensayo
  let formAgregarEnsayoConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "codigo",
            tipoitemformularioid: 1,
            etiqueta: "Código del ensayo",
            requerido: true,
            respuesta: modoEdicion ? reporteSeleccionado?.codigo : null,
          },
          {
            nombre: "fechaemision",
            tipoitemformularioid: 7,
            etiqueta: "Fecha de emisión",
            requerido: true,
            respuesta: modoEdicion
              ? new Date(reporteSeleccionado?.fechaemision)
              : null,
          },
          {
            nombre: "laboratorioid",
            tipoitemformularioid: 5,
            etiqueta: "Laboratorio",
            requerido: true,
            opciones: `colab/actividad/laboratorios?norma=${normaId}`,
            sinOpcionInicial: true,
            respuesta: modoEdicion
              ? reporteSeleccionado?.laboratorio?.id
              : null,
          },
        ],
      },
    ],
    botones: [
      {
        etiqueta: modoEdicion ? "Actualizar ensayo" : "Registrar ensayo",
        clases: "verde",
        validar: true,
        callback: modoEdicion ? submitEditarEnsayo : submitAgregarEnsayo,
      },
    ],
  };

  // Función ejecutada al agregar un ensayo
  function submitAgregarEnsayo(contenidoCampos) {
    // Prepara los datos con los tipos correctos
    const datosEnsayo = {
      codigo: contenidoCampos.codigo,
      fechaemision: contenidoCampos.fechaemision,
      laboratorioid: parseInt(contenidoCampos.laboratorioid),
      pasoId,
    };

    setLoaderAgregarEnsayo(true);

    accessAPI(
      "POST",
      `colab/actividad/${pasoId}/ensayo`,
      datosEnsayo,
      (respuesta) => {
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Ensayo registrado correctamente",
          life: 3000,
        });
        setSideBar(false);
        setLoaderAgregarEnsayo(false);
        setReportes((actual) => [...actual, respuesta]);
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0].msg ||
            "Ocurrió un error, intente nuevamente más tarde",
          sticky: true,
        });
        setLoaderAgregarEnsayo(false);
      }
    );
  }

  // Función ejecutada al editar un ensayo
  function submitEditarEnsayo(contenidoCampos) {
    // Prepara los datos con los tipos correctos
    const datosEnsayo = {
      codigo: contenidoCampos.codigo,
      fechaemision: contenidoCampos.fechaemision,
      laboratorioid: parseInt(contenidoCampos.laboratorioid),
    };

    setLoaderAgregarEnsayo(true);

    accessAPI(
      "PUT",
      `colab/actividad/${pasoId}/ensayo/${reporteSeleccionado.id}`,
      datosEnsayo,
      (respuesta) => {
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Ensayo actualizado correctamente",
          life: 3000,
        });
        setSideBar(false);
        setLoaderAgregarEnsayo(false);
        // Actualiza el reporte en la lista
        setReportes((actual) =>
          actual.map((reporte) =>
            reporte.id === reporteSeleccionado.id ? respuesta : reporte
          )
        );
        setModoEdicion(false);
        setReporteSeleccionado(null);
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0].msg ||
            "Ocurrió un error, intente nuevamente más tarde",
          sticky: true,
        });
        setLoaderAgregarEnsayo(false);
      }
    );
  }

  // Función para abrir el sidebar en modo edición
  function abrirEdicion(reporte) {
    setReporteSeleccionado(reporte);
    setModoEdicion(true);
    setSideBar(true);
  }

  // Función para abrir el sidebar en modo creación
  function abrirCreacion() {
    setReporteSeleccionado(null);
    setModoEdicion(false);
    setSideBar(true);
  }

  // Función para eliminar un reporte de ensayo
  function eliminarReporte(reporte) {
    accessAPI(
      "DELETE",
      `colab/actividad/${pasoId}/ensayo/${reporte.id}`,
      null,
      () => {
        setReportes((actual) => actual.filter((r) => r.id !== reporte.id));
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Reporte de ensayo eliminado",
          life: 3000,
        });
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg ||
            "Ocurrió un error al eliminar el reporte",
          sticky: true,
        });
      }
    );
  }

  // Items del menú contextual
  const getMenuItems = () => {
    if (!reporteMenu) return [];
    return [
      {
        label: "Editar",
        icon: "pi pi-pencil",
        command: () => abrirEdicion(reporteMenu),
      },
      {
        label: "Eliminar",
        icon: "pi pi-trash",
        command: () => {
          confirmDialog({
            message: "¿Está seguro que desea eliminar este reporte de ensayo?",
            header: "Confirmar eliminación",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sí",
            rejectLabel: "No",
            accept: () => eliminarReporte(reporteMenu),
          });
        },
      },
    ];
  };
  return (
    <div css={{ position: "relative" }}>
      <Toast ref={toast} position="top-center" />
      <Menu model={getMenuItems()} popup ref={menuRef} />
      <Sidebar
        visible={sideBar}
        onHide={() => setSideBar(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>
            {modoEdicion ? "Editar reporte de ensayo" : "Nuevo reporte de ensayo"}
          </Titulo>
          <Formulario
            key={modoEdicion ? `editar-${reporteSeleccionado?.id}` : "crear"}
            config={formAgregarEnsayoConfig}
            loader={loaderAgregarEnsayo}
          />
        </div>
      </Sidebar>
      <Titulo
        botones={
          !modoLectura && (
            <Boton callback={abrirCreacion} texto icono chico negro>
              <i className="pi pi-plus"></i>
            </Boton>
          )
        }
      >
        <i className="pi pi-file-edit" css={{ marginRight: "0.5em" }}></i>
        Reportes de ensayo
      </Titulo>
      <TablaFiltrable conFiltros={false}
        value={reportes}
        emptyMessage="No hay archivos cargados"
        size="small"
      >
        <Column field="codigo" header="Código"></Column>
        <Column
          field={(reporte) => formatFecha(reporte.fechaemision)}
          header="Emisión"
        ></Column>
        <Column
          field={(reporte) =>
            reporte?.laboratorio?.nombrefantasia
              ? reporte.laboratorio.nombrefantasia
              : reporte?.laboratorio?.razonsocial
          }
          header="Laboratorio "
        ></Column>
        {!modoLectura && (
          <Column
            body={(reporte) => (
              <Boton
                callback={(e) => {
                  setReporteMenu(reporte);
                  menuRef.current.toggle(e);
                }}
                icono
                chico
                negro
              >
                <i className="pi pi-ellipsis-v"></i>
              </Boton>
            )}
            style={{ width: "3em", textAlign: "center" }}
          />
        )}
      </TablaFiltrable>
    </div>
  );
}
